/**
 * The log of settlement runs.
 *
 * Deliberately below the seller table. History answers "what happened?", which
 * is the second question an operator has; "who is owed what right now?" is the
 * first, and it used to sit underneath this.
 */
import type { SettlementRunSummary } from '@terra/api-client';
import { EmptyState, Skeleton } from '@terra/ui';

import { formatDate, formatDateTime, formatPaise } from '../../lib/money';

import { StagePill } from './StagePill';

type Run = SettlementRunSummary;

export function SettlementRuns({
  runs,
  isLoading,
  isError,
  onRetry,
}: {
  runs: Run[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const latest = runs?.[0];

  return (
    <section aria-label="Settlement runs" className="space-y-4">
      <div>
        <h2 className="text-ink text-[17px] font-bold">Settlement runs</h2>
        <p className="text-ink-muted mt-0.5 text-[13px] font-medium">
          Each run settles every order whose return window has closed. The scheduled one is 07:30
          IST daily.
        </p>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 rounded-2xl" />
      ) : isError ? (
        <div className="border-border/60 bg-surface rounded-2xl border p-8 text-center shadow-sm">
          <p className="text-ink text-[14px] font-bold">Unable to load settlement runs</p>
          <button
            type="button"
            onClick={onRetry}
            className="bg-ink hover:bg-ink/90 mt-3 h-9 rounded-lg px-4 text-[12.5px] font-bold text-white"
          >
            Retry
          </button>
        </div>
      ) : !runs || runs.length === 0 ? (
        <EmptyState
          title="No settlement runs yet"
          description="The first run will appear here once an order's return window closes and the scheduled job settles it."
        />
      ) : (
        <>
          {latest && (
            <div className="border-border/60 bg-surface flex flex-wrap items-center justify-between gap-4 rounded-2xl border p-5 shadow-sm">
              <div>
                <p className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                  Latest run
                </p>
                <p className="text-ink mt-1 text-[14px] font-bold">
                  {formatDateTime(latest.startedAt)}
                </p>
                <p className="text-ink-muted/70 mt-0.5 font-mono text-[11px]">{latest.id}</p>
              </div>
              <div className="flex flex-wrap items-center gap-6">
                {[
                  { label: 'Sellers', value: String(latest.totals?.vendorCount ?? '—') },
                  { label: 'Sub-orders', value: String(latest.totals?.subOrderCount ?? '—') },
                  { label: 'Net settled', value: formatPaise(latest.totals?.netPaise ?? 0) },
                ].map((s) => (
                  <div key={s.label}>
                    <p className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                      {s.label}
                    </p>
                    <p className="text-ink mt-0.5 text-[14px] font-bold tabular-nums">{s.value}</p>
                  </div>
                ))}
                <StagePill tone={latest.completedAt ? 'emerald' : 'sky'}>{latest.status}</StagePill>
              </div>
            </div>
          )}

          <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="px-5 py-4">Run date</th>
                    <th className="px-5 py-4 text-right">Sellers</th>
                    <th className="px-5 py-4 text-right">Sub-orders</th>
                    <th className="px-5 py-4 text-right">Commission</th>
                    <th className="px-5 py-4 text-right">TCS</th>
                    <th className="px-5 py-4 text-right">TDS</th>
                    <th className="px-5 py-4 text-right">Reserve</th>
                    <th className="px-5 py-4 text-right">Net</th>
                    <th className="px-5 py-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {runs.map((r) => (
                    <tr key={r.id} className="hover:bg-surface-subtle/30 transition-colors">
                      <td className="text-ink px-5 py-4 font-medium">{formatDate(r.runDate)}</td>
                      <td className="px-5 py-4 text-right tabular-nums">
                        {r.totals?.vendorCount ?? 0}
                      </td>
                      <td className="px-5 py-4 text-right tabular-nums">
                        {r.totals?.subOrderCount ?? 0}
                      </td>
                      <td className="px-5 py-4 text-right tabular-nums">
                        {formatPaise(r.totals?.commissionPaise ?? 0)}
                      </td>
                      <td className="px-5 py-4 text-right tabular-nums">
                        {formatPaise(r.totals?.tcsPaise ?? 0)}
                      </td>
                      <td className="px-5 py-4 text-right tabular-nums">
                        {formatPaise(r.totals?.tdsPaise ?? 0)}
                      </td>
                      <td className="px-5 py-4 text-right tabular-nums">
                        {formatPaise(r.totals?.reservePaise ?? 0)}
                      </td>
                      <td className="text-ink px-5 py-4 text-right font-bold tabular-nums">
                        {formatPaise(r.totals?.netPaise ?? 0)}
                      </td>
                      <td className="px-5 py-4">
                        <StagePill tone={r.completedAt ? 'emerald' : 'sky'}>{r.status}</StagePill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
